import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Clock,
  Check,
  AlertCircle,
  Play,
  RefreshCw,
  Plus,
  X,
  Calendar,
  ChevronDown,
  Search,
  FileText,
  Layers,
  ArrowLeft,
  Send,
  Copy,
  Trash2,
  Share2,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { AI_EMPLOYEES } from '../data/employees';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import ScheduledPosts from '../components/ScheduledPosts';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Simple markdown to formatted JSX converter
const formatMarkdownOutput = (text: string, isDark: boolean): React.ReactNode => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip horizontal rules (---)
    if (line.trim().match(/^-{3,}$/)) {
      elements.push(<div key={key++} className="my-4 border-t border-current opacity-20" />);
      continue;
    }

    // Headers (### Header) - render as bold
    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      elements.push(
        <div key={key++} className="font-bold text-base mt-4 mb-2">
          {formatInlineMarkdown(headerMatch[1], isDark)}
        </div>
      );
      continue;
    }

    // Empty lines - add spacing
    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    // Regular text - format inline markdown (bold, links)
    elements.push(
      <div key={key++} className="mb-1">
        {formatInlineMarkdown(line, isDark)}
      </div>
    );
  }

  return elements;
};

// Format inline markdown (bold text, links)
const formatInlineMarkdown = (text: string, isDark: boolean): React.ReactNode => {
  // Match **bold** and [link](url) patterns
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let partKey = 0;

  while (remaining.length > 0) {
    // Check for bold **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Check for links [text](url)
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    // Find which comes first
    const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : -1;
    const linkIndex = linkMatch ? remaining.indexOf(linkMatch[0]) : -1;

    if (boldIndex === -1 && linkIndex === -1) {
      // No more patterns, add remaining text
      parts.push(<span key={partKey++}>{remaining}</span>);
      break;
    }

    // Determine which pattern comes first
    let firstIndex = -1;
    let matchType: 'bold' | 'link' | null = null;

    if (boldIndex !== -1 && (linkIndex === -1 || boldIndex < linkIndex)) {
      firstIndex = boldIndex;
      matchType = 'bold';
    } else if (linkIndex !== -1) {
      firstIndex = linkIndex;
      matchType = 'link';
    }

    // Add text before the match
    if (firstIndex > 0) {
      parts.push(<span key={partKey++}>{remaining.substring(0, firstIndex)}</span>);
    }

    // Add the matched element
    if (matchType === 'bold' && boldMatch) {
      parts.push(<span key={partKey++} className="font-semibold">{boldMatch[1]}</span>);
      remaining = remaining.substring(firstIndex + boldMatch[0].length);
    } else if (matchType === 'link' && linkMatch) {
      parts.push(
        <a
          key={partKey++}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline ${isDark ? 'text-accent-cyan hover:text-accent-cyan/80' : 'text-accent-purple hover:text-accent-purple/80'}`}
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.substring(firstIndex + linkMatch[0].length);
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Task {
  id: number;
  employee_id: string;
  use_case_id?: string;
  template_id?: string;
  title: string;
  description?: string;
  status: 'pending' | 'scheduled' | 'running' | 'completed' | 'failed' | 'cancelled';
  input_data?: string;
  output_data?: string;
  error_message?: string;
  credits_used: number;
  progress?: number;
  progress_message?: string;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

interface Stats {
  total: number;
  completed: number;
  running: number;
  pending: number;
  failed: number;
  total_credits_used: number;
}

interface TaskCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  employee_id: string;
  estimated_credits: number;
  tags: string[];
  input_fields?: InputField[];
  prompt_template?: string;
}

interface InputField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'url' | 'email';
  placeholder?: string;
  required: boolean;
  options?: { value: string; label: string }[];
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface TasksResponse {
  items: Task[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface TemplatesResponse {
  categories: TaskCategory[];
  templates: TaskTemplate[];
  total: number;
}

type ViewMode = 'tasks' | 'templates' | 'template-form' | 'schedule';

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_username?: string;
  provider_icon?: string;
  provider_color?: string;
  provider_category?: string;
  status: string;
}

export default function Tasks() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'scheduled' | 'running' | 'completed' | 'failed'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Templates state
  const [viewMode, setViewMode] = useState<ViewMode>('tasks');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [templateInputs, setTemplateInputs] = useState<Record<string, string>>({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Send to integration state
  const [showSendModal, setShowSendModal] = useState(false);
  const [taskToSend, setTaskToSend] = useState<Task | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // New task form
  const [newTask, setNewTask] = useState({
    employee_id: '',
    title: '',
    description: '',
    scheduled_at: '',
  });

  const loadTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);

      const response = await fetch(`${API_URL}/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TasksResponse>;
      if (data.success && data.data) {
        setTasks(data.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  }, [token, filter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/tasks/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Stats>;
      if (data.success && data.data) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (templateSearch) params.append('search', templateSearch);

      const response = await fetch(`${API_URL}/tasks/templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TemplatesResponse>;
      if (data.success && data.data) {
        setTemplates(data.data.templates || []);
        if (!categories.length && data.data.categories) {
          setCategories(data.data.categories);
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [token, selectedCategory, templateSearch, categories.length]);

  const loadTemplateDetails = async (templateId: string) => {
    try {
      const response = await fetch(`${API_URL}/tasks/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TaskTemplate>;
      if (data.success && data.data) {
        setSelectedTemplate(data.data);
        setTemplateInputs({});
        setViewMode('template-form');
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadTasks(), loadStats()]);
      setIsLoading(false);
    };
    init();
  }, [loadTasks, loadStats]);

  useEffect(() => {
    loadTasks();
  }, [filter, loadTasks]);

  useEffect(() => {
    if (viewMode === 'templates') {
      loadTemplates();
    }
  }, [viewMode, loadTemplates]);

  // Poll for running tasks
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running' || t.status === 'pending');
    if (!hasRunning) return;

    const interval = setInterval(() => {
      loadTasks();
      loadStats();
    }, 3000);

    return () => clearInterval(interval);
  }, [tasks, loadTasks, loadStats]);

  const createTask = async () => {
    if (!newTask.employee_id || !newTask.title) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: newTask.employee_id,
          title: newTask.title,
          description: newTask.description || undefined,
          scheduled_at: newTask.scheduled_at || undefined,
        }),
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success && data.data) {
        setTasks(prev => [data.data!, ...prev]);
        setShowCreateModal(false);
        setNewTask({ employee_id: '', title: '', description: '', scheduled_at: '' });
        loadStats();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const createTaskFromTemplate = async () => {
    if (!selectedTemplate) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          input_data: templateInputs,
        }),
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success && data.data) {
        setTasks(prev => [data.data!, ...prev]);
        setViewMode('tasks');
        setSelectedTemplate(null);
        setTemplateInputs({});
        loadStats();
      }
    } catch (error) {
      console.error('Failed to create task from template:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const cancelTask = async (taskId: number) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success) {
        loadTasks();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to cancel task:', error);
    }
  };

  const retryTask = async (taskId: number) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<unknown>;
      if (data.success) {
        loadTasks();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to retry task:', error);
    }
  };

  const deleteTask = async (taskId: number) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<unknown>;
      if (data.success) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        setSelectedTask(null);
        loadStats();
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const copyOutput = (task: Task) => {
    if (task.output_data) {
      try {
        const output = JSON.parse(task.output_data);
        navigator.clipboard.writeText(output.result || '');
      } catch {
        navigator.clipboard.writeText(task.output_data);
      }
    }
  };

  const loadIntegrations = async () => {
    setIsLoadingIntegrations(true);
    try {
      const response = await fetch(`${API_URL}/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Integration[]>;
      if (data.success && data.data) {
        // Filter only connected integrations that support posting
        const postableProviders = ['twitter', 'facebook', 'linkedin', 'slack', 'discord', 'instagram', 'gmail'];
        const connected = data.data.filter(
          (i) => i.status === 'connected' && postableProviders.includes(i.provider)
        );
        setIntegrations(connected);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const openSendModal = (task: Task) => {
    setTaskToSend(task);
    setShowSendModal(true);
    setSendSuccess(null);
    setSendError(null);
    loadIntegrations();
  };

  const sendToIntegration = async (integrationId: number, provider: string) => {
    if (!taskToSend?.output_data) return;

    setIsSending(true);
    setSendError(null);
    setSendSuccess(null);

    try {
      let content = '';
      try {
        const output = JSON.parse(taskToSend.output_data);
        content = output.result || taskToSend.output_data;
      } catch {
        content = taskToSend.output_data;
      }

      const response = await fetch(`${API_URL}/tasks/${taskToSend.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          integration_id: integrationId,
          provider,
          content,
        }),
      });

      const data = (await response.json()) as APIResponse<{ message: string }>;
      if (data.success) {
        setSendSuccess(`Successfully sent to ${provider}!`);
        setTimeout(() => {
          setShowSendModal(false);
          setTaskToSend(null);
          setSendSuccess(null);
        }, 2000);
      } else {
        setSendError(data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Failed to send to integration:', error);
      setSendError('Failed to send. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const getProviderIcon = (provider: string): string => {
    const icons: Record<string, string> = {
      twitter: '𝕏',
      facebook: 'f',
      linkedin: 'in',
      instagram: '📷',
      slack: '#',
      discord: '🎮',
      gmail: '✉',
    };
    return icons[provider] || '🔗';
  };

  const getProviderColor = (provider: string): string => {
    const colors: Record<string, string> = {
      twitter: 'from-slate-800 to-slate-900',
      facebook: 'from-blue-600 to-blue-700',
      linkedin: 'from-blue-500 to-blue-600',
      instagram: 'from-pink-500 to-purple-600',
      slack: 'from-purple-500 to-pink-500',
      discord: 'from-indigo-500 to-purple-600',
      gmail: 'from-red-500 to-orange-500',
    };
    return colors[provider] || 'from-gray-500 to-gray-600';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-accent-green/20 text-accent-green';
      case 'running':
        return 'bg-accent-cyan/20 text-accent-cyan';
      case 'pending':
      case 'scheduled':
        return 'bg-accent-orange/20 text-accent-orange';
      case 'failed':
        return 'bg-accent-red/20 text-accent-red';
      case 'cancelled':
        return 'bg-dark-700 text-text-muted';
      default:
        return 'bg-dark-700 text-text-muted';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-3 h-3" />;
      case 'running':
        return <RefreshCw className="w-3 h-3 animate-spin" />;
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'scheduled':
        return <Calendar className="w-3 h-3" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3" />;
      case 'cancelled':
        return <X className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getCategoryColor = (color: string) => {
    const colors: Record<string, string> = {
      purple: 'from-accent-purple to-accent-purple/50',
      blue: 'from-blue-500 to-blue-500/50',
      pink: 'from-pink-500 to-pink-500/50',
      green: 'from-accent-green to-accent-green/50',
      cyan: 'from-accent-cyan to-accent-cyan/50',
      orange: 'from-accent-orange to-accent-orange/50',
      yellow: 'from-yellow-500 to-yellow-500/50',
      teal: 'from-teal-500 to-teal-500/50',
      indigo: 'from-indigo-500 to-indigo-500/50',
      emerald: 'from-emerald-500 to-emerald-500/50',
      slate: 'from-slate-500 to-slate-500/50',
      violet: 'from-violet-500 to-violet-500/50',
    };
    return colors[color] || colors.purple;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-xl sm:text-2xl font-bold mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Tasks</h1>
          <p className={`text-sm sm:text-base ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Manage and automate your AI-powered tasks
          </p>
        </div>
        <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-3">
          <button
            onClick={() => setViewMode(viewMode === 'schedule' ? 'tasks' : 'schedule')}
            className={`btn-secondary flex items-center justify-center space-x-2 text-sm sm:text-base ${viewMode === 'schedule' ? 'bg-accent-cyan/20 text-accent-cyan' : ''}`}
          >
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Schedule</span>
          </button>
          <button
            onClick={() => setViewMode(viewMode === 'templates' ? 'tasks' : 'templates')}
            className={`btn-secondary flex items-center justify-center space-x-2 text-sm sm:text-base ${viewMode === 'templates' ? 'bg-accent-purple/20 text-accent-purple' : ''}`}
          >
            <Layers className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Templates</span>
            <span className="text-xs bg-accent-purple/30 px-2 py-0.5 rounded-full">{templates.length > 0 ? templates.length : '100+'}</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>New Task</span>
          </button>
        </div>
      </motion.div>

      {/* View Content */}
      <AnimatePresence mode="wait">
        {viewMode === 'tasks' && (
          <motion.div
            key="tasks"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { label: 'Total Tasks', value: stats?.total || 0, icon: Zap, color: 'text-accent-purple' },
                { label: 'Completed', value: stats?.completed || 0, icon: Check, color: 'text-accent-green' },
                { label: 'Running', value: stats?.running || 0, icon: Play, color: 'text-accent-cyan' },
                { label: 'Pending', value: stats?.pending || 0, icon: Clock, color: 'text-accent-orange' },
              ].map((stat) => (
                <div key={stat.label} className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                      <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
                    </div>
                    <stat.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${stat.color} opacity-50`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap overflow-x-auto pb-1 -mx-1 px-1">
              {['all', 'pending', 'scheduled', 'running', 'completed', 'failed'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f as typeof filter)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                    filter === f
                      ? 'bg-gradient-accent text-white'
                      : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Tasks List */}
            <div className="space-y-2 sm:space-y-3">
              {tasks.map((task) => {
                const employee = AI_EMPLOYEES.find((e) => e.id === task.employee_id);

                return (
                  <div
                    key={task.id}
                    className={`rounded-xl sm:rounded-2xl p-4 sm:p-6 border transition-colors cursor-pointer ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/30' : 'bg-white border-slate-200 hover:border-accent-purple/30 shadow-sm'}`}
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 min-w-0">
                        {employee && (
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center flex-shrink-0`}
                          >
                            <span className="text-xs sm:text-sm font-bold text-white">
                              {employee.name[0]}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className={`font-medium text-sm sm:text-base truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{task.title}</p>
                          <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            {employee?.name} • {formatDate(task.created_at)}
                          </p>
                          {task.scheduled_at && task.status === 'scheduled' && (
                            <p className="text-xs text-accent-orange mt-0.5">
                              Scheduled: {formatDate(task.scheduled_at)}
                            </p>
                          )}
                          {task.status === 'running' && task.progress_message && (
                            <div className="flex items-center gap-2 mt-1">
                              <div className={`w-20 sm:w-32 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                                <div
                                  className="h-full bg-accent-cyan transition-all duration-500"
                                  style={{ width: `${task.progress || 0}%` }}
                                />
                              </div>
                              <span className="text-xs text-accent-cyan truncate">{task.progress_message}</span>
                            </div>
                          )}
                          {task.error_message && (
                            <p className="text-xs sm:text-sm text-accent-red mt-1 truncate">{task.error_message}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:space-x-4 pl-11 sm:pl-0">
                        {/* Credits - hidden on mobile */}
                        <span className={`text-xs sm:text-sm hidden sm:inline ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          {task.credits_used || 0} credits
                        </span>

                        {/* Status */}
                        <span
                          className={`badge flex items-center gap-1 text-xs ${getStatusColor(task.status)}`}
                        >
                          {getStatusIcon(task.status)}
                          <span className="hidden xs:inline">{task.status}</span>
                        </span>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          {task.status === 'completed' && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyOutput(task);
                                }}
                                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                                title="Copy output"
                              >
                                <Copy className={`w-3.5 h-3.5 sm:w-4 sm:h-4 hover:text-accent-green ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSendModal(task);
                                }}
                                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                                title="Send to integration"
                              >
                                <Share2 className={`w-3.5 h-3.5 sm:w-4 sm:h-4 hover:text-accent-purple ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                              </button>
                            </>
                          )}
                          {task.status === 'failed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                retryTask(task.id);
                              }}
                              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                              title="Retry task"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 hover:text-accent-cyan ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                            </button>
                          )}
                          {(task.status === 'pending' || task.status === 'scheduled') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelTask(task.id);
                              }}
                              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                              title="Cancel task"
                            >
                              <X className={`w-3.5 h-3.5 sm:w-4 sm:h-4 hover:text-accent-red ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                            </button>
                          )}
                          {/* Delete button - works for all statuses */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTask(task.id);
                            }}
                            className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                            title="Delete task"
                          >
                            <Trash2 className={`w-3.5 h-3.5 sm:w-4 sm:h-4 hover:text-accent-red ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {tasks.length === 0 && (
              <div className="text-center py-16">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-dark-800' : 'bg-slate-100'}`}>
                  <Zap className={`w-8 h-8 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No tasks found</h3>
                <p className={`mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                  {filter === 'all' ? 'Create your first task to get started' : `No ${filter} tasks`}
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setViewMode('templates')}
                    className="btn-secondary"
                  >
                    Browse Templates
                  </button>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn-primary"
                  >
                    Create Task
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {viewMode === 'templates' && (
          <motion.div
            key="templates"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              <input
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder={`Search ${templates.length > 0 ? templates.length : '100+'} templates...`}
                className={`w-full border rounded-xl pl-12 pr-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-800 border-dark-700 text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
              />
            </div>

            {/* Categories */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? 'bg-gradient-accent text-white'
                    : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-accent text-white'
                      : isDark ? 'bg-dark-800 text-text-secondary hover:bg-dark-700' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Templates Grid */}
            {isLoadingTemplates ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => {
                  const category = categories.find(c => c.id === template.category);
                  const employee = AI_EMPLOYEES.find(e => e.id === template.employee_id);

                  return (
                    <div
                      key={template.id}
                      onClick={() => loadTemplateDetails(template.id)}
                      className={`rounded-2xl p-6 border transition-all cursor-pointer group ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/30' : 'bg-white border-slate-200 hover:border-accent-purple/30 shadow-sm'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getCategoryColor(category?.color || 'purple')} flex items-center justify-center`}>
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'text-text-muted bg-dark-700' : 'text-slate-500 bg-slate-100'}`}>
                          ~{template.estimated_credits} credits
                        </span>
                      </div>

                      <h3 className={`font-semibold mb-1 group-hover:text-accent-purple transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {template.name}
                      </h3>
                      <p className={`text-sm mb-3 line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        {template.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {employee && (
                            <div
                              className={`w-6 h-6 rounded-full bg-gradient-to-br ${employee.gradient} flex items-center justify-center`}
                            >
                              <span className="text-xs font-bold text-white">
                                {employee.name[0]}
                              </span>
                            </div>
                          )}
                          <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{employee?.name}</span>
                        </div>
                        <span className={`text-xs capitalize ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{category?.name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {templates.length === 0 && !isLoadingTemplates && (
              <div className="text-center py-16">
                <FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No templates found</h3>
                <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>Try a different search or category</p>
              </div>
            )}
          </motion.div>
        )}

        {viewMode === 'template-form' && selectedTemplate && (
          <motion.div
            key="template-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Back button */}
            <button
              onClick={() => {
                setViewMode('templates');
                setSelectedTemplate(null);
              }}
              className={`flex items-center gap-2 transition-colors ${isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Templates
            </button>

            <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedTemplate.name}</h2>
                  <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>{selectedTemplate.description}</p>
                </div>
                <span className={`text-sm px-3 py-1.5 rounded-lg ${isDark ? 'text-text-muted bg-dark-700' : 'text-slate-500 bg-slate-100'}`}>
                  ~{selectedTemplate.estimated_credits} credits
                </span>
              </div>

              {/* Input Fields */}
              <div className="space-y-4 mb-6">
                {selectedTemplate.input_fields?.map((field) => (
                  <div key={field.name}>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      {field.label}
                      {field.required && <span className="text-accent-red ml-1">*</span>}
                    </label>

                    {field.type === 'textarea' ? (
                      <textarea
                        value={templateInputs[field.name] || ''}
                        onChange={(e) => setTemplateInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={field.placeholder}
                        rows={4}
                        className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none resize-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                      />
                    ) : field.type === 'select' ? (
                      <div className="relative">
                        <select
                          value={templateInputs[field.name] || ''}
                          onChange={(e) => setTemplateInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                          className={`w-full border rounded-xl px-4 py-3 appearance-none cursor-pointer focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                        >
                          <option value="">Select...</option>
                          {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                      </div>
                    ) : (
                      <input
                        type={field.type}
                        value={templateInputs[field.name] || ''}
                        onChange={(e) => setTemplateInputs(prev => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={field.placeholder}
                        className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Submit */}
              <button
                onClick={createTaskFromTemplate}
                disabled={isCreating}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Run Task
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {viewMode === 'schedule' && (
          <motion.div
            key="schedule"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ScheduledPosts />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-md w-full border ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Create New Task</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Employee Select */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  AI Employee
                </label>
                <div className="relative">
                  <select
                    value={newTask.employee_id}
                    onChange={(e) => setNewTask(prev => ({ ...prev, employee_id: e.target.value }))}
                    className={`w-full border rounded-xl px-4 py-3 appearance-none cursor-pointer focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                  >
                    <option value="">Select an employee</option>
                    {AI_EMPLOYEES.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
                    ))}
                  </select>
                  <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Task Title
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Generate weekly social media content"
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Description (optional)
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add more details about what you want..."
                  rows={3}
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none resize-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>

              {/* Schedule */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Schedule (optional)
                </label>
                <input
                  type="datetime-local"
                  value={newTask.scheduled_at}
                  onChange={(e) => setNewTask(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={createTask}
                  disabled={!newTask.employee_id || !newTask.title || isCreating}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    'Create Task'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-2xl w-full border max-h-[80vh] overflow-y-auto ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedTask.title}</h2>
              <button
                onClick={() => setSelectedTask(null)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-4">
                <span className={`badge flex items-center gap-1 ${getStatusColor(selectedTask.status)}`}>
                  {getStatusIcon(selectedTask.status)}
                  {selectedTask.status}
                </span>
                <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {selectedTask.credits_used || 0} credits used
                </span>
              </div>

              {/* Progress */}
              {selectedTask.status === 'running' && (
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{selectedTask.progress_message || 'Processing...'}</span>
                    <span className="text-sm text-accent-cyan">{selectedTask.progress || 0}%</span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-full bg-accent-cyan transition-all duration-500"
                      style={{ width: `${selectedTask.progress || 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Output */}
              {selectedTask.status === 'completed' && selectedTask.output_data && (
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-sm font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Output</h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => copyOutput(selectedTask)}
                        className="text-sm text-accent-purple hover:text-accent-purple/80"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTask(null);
                          openSendModal(selectedTask);
                        }}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 flex items-center gap-1"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Send
                      </button>
                    </div>
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {(() => {
                      try {
                        const output = JSON.parse(selectedTask.output_data);
                        const result = output.result || 'No output';
                        return formatMarkdownOutput(result, isDark);
                      } catch {
                        return formatMarkdownOutput(selectedTask.output_data, isDark);
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Error */}
              {selectedTask.error_message && (
                <div className="bg-accent-red/10 border border-accent-red/20 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-accent-red mb-1">Error</h3>
                  <p className="text-sm text-accent-red/80">{selectedTask.error_message}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>Created:</span>
                  <span className={`ml-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDate(selectedTask.created_at)}</span>
                </div>
                {selectedTask.completed_at && (
                  <div>
                    <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>Completed:</span>
                    <span className={`ml-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDate(selectedTask.completed_at)}</span>
                  </div>
                )}
              </div>

              {/* Delete Button */}
              <div className="pt-4 border-t border-border mt-4">
                <button
                  onClick={() => deleteTask(selectedTask.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-accent-red hover:bg-accent-red/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Task
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Send to Integration Modal */}
      {showSendModal && taskToSend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-md w-full border ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Send to Integration</h2>
              <button
                onClick={() => {
                  setShowSendModal(false);
                  setTaskToSend(null);
                  setSendSuccess(null);
                  setSendError(null);
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            {/* Task Preview */}
            <div className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
              <p className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{taskToSend.title}</p>
              <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {(() => {
                  try {
                    const output = JSON.parse(taskToSend.output_data || '{}');
                    const result = output.result || '';
                    return result.substring(0, 100) + (result.length > 100 ? '...' : '');
                  } catch {
                    return (taskToSend.output_data || '').substring(0, 100);
                  }
                })()}
              </p>
            </div>

            {/* Success Message */}
            {sendSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-accent-green/10 border border-accent-green/20">
                <p className="text-sm text-accent-green flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {sendSuccess}
                </p>
              </div>
            )}

            {/* Error Message */}
            {sendError && (
              <div className="mb-4 p-3 rounded-lg bg-accent-red/10 border border-accent-red/20">
                <p className="text-sm text-accent-red flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {sendError}
                </p>
              </div>
            )}

            {/* Loading Integrations */}
            {isLoadingIntegrations && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent-purple" />
              </div>
            )}

            {/* No Integrations */}
            {!isLoadingIntegrations && integrations.length === 0 && (
              <div className="text-center py-8">
                <Share2 className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <p className={`text-sm mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  No connected integrations
                </p>
                <p className={`text-xs mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                  Connect your social media accounts or apps to send task outputs directly.
                </p>
                <a
                  href="/dashboard/integrations"
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Connect Integrations
                </a>
              </div>
            )}

            {/* Integration List */}
            {!isLoadingIntegrations && integrations.length > 0 && !sendSuccess && (
              <div className="space-y-2">
                <p className={`text-sm mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Select where to send this content:
                </p>
                {integrations.map((integration) => (
                  <button
                    key={integration.id}
                    onClick={() => sendToIntegration(integration.id, integration.provider)}
                    disabled={isSending}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      isDark
                        ? 'bg-dark-900 border-dark-600 hover:border-accent-purple/50'
                        : 'bg-white border-slate-200 hover:border-accent-purple/50'
                    } ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getProviderColor(integration.provider)} flex items-center justify-center text-white font-bold`}>
                      {getProviderIcon(integration.provider)}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {integration.provider_name || integration.provider}
                      </p>
                      {integration.provider_username && (
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          @{integration.provider_username}
                        </p>
                      )}
                    </div>
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin text-accent-purple" />
                    ) : (
                      <Send className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
